import { useRef, useEffect, useMemo } from "react";
/**
 * Custom hook that memoizes a value and provides cleanup functionality
 * when the value needs to be recomputed due to changes in dependencies.
 *
 * @param computeFn Function to compute the memoized value.
 * @param cleanupFn Function to clean up the old memoized value.
 * @param deps Array of dependencies for re-computation.
 * @returns The computed memoized value.
 */ export function useMemoWithCleanup(computeFn, cleanupFn, deps) {
    // Ref to store the current value for access during cleanup
    const currentValueRef = useRef();
    // Compute the memoized value
    const memoizedValue = useMemo(()=>{
        // Compute the new value
        const newValue = computeFn();
        // Return the computed value
        return newValue;
    }, deps);
    // Use an effect for cleanup when dependencies change or on unmount
    useEffect(()=>{
        // Return a cleanup function that runs when deps change or on unmount
        return ()=>{
            // Execute cleanup function with the old value
            if (currentValueRef.current !== undefined) {
                cleanupFn(currentValueRef.current);
            }
        };
    }, deps) // Include all external deps in this array
    ;
    // Update the ref after cleanup but within the same effect cycle
    useEffect(()=>{
        currentValueRef.current = memoizedValue;
    }, [
        memoizedValue
    ]) // This effect updates the ref whenever the memoized value changes
    ;
    // Return the memoized value
    return memoizedValue;
}
